import { apiPath } from "../../lib/apiPath";
import type { CampaignAttempt, CampaignNodeView, CampaignTreeResponse } from "./types";

const jsonHeaders = { "Content-Type": "application/json", Accept: "application/json" };

export async function fetchCampaignTree(): Promise<CampaignTreeResponse | null> {
  const r = await fetch(apiPath("/api/campaign"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`campaign ${r.status}`);
  return (await r.json()) as CampaignTreeResponse;
}

export type CampaignStartResult = {
  ok: boolean;
  attempt: CampaignAttempt;
  label: string;
  alreadyCompleted: boolean;
};

export async function postCampaignStart(nodeId: string): Promise<CampaignStartResult> {
  const r = await fetch(apiPath("/api/campaign"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "start", nodeId }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || `start ${r.status}`), { data, status: r.status });
  return data as CampaignStartResult;
}

export async function postCampaignAbandon(): Promise<void> {
  await fetch(apiPath("/api/campaign"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "abandon" }),
  }).catch(() => {});
}

export type CampaignClaimResult = {
  ok: boolean;
  alreadyCompleted: boolean;
  alreadyClaimed: boolean;
  rewardGranted: boolean;
  rewardType: "dots" | "item" | "both";
  rewardDots: number;
  rewardCosmeticId: string | null;
  rewardCosmeticName: string | null;
  rewardCosmeticImageData?: string | null;
  rewardCosmeticAnimationFrames?: string[] | null;
  rewardCosmeticNewCursor?: boolean;
  rewardCosmeticCategory?: string | null;
  dotsBalance: number;
  ownedCosmeticIds: string[];
  completedNodeIds: string[];
  claimedNodeIds: string[];
};

export async function postCampaignClaim(
  token: string,
  progress: { score: number; timeSec: number },
): Promise<CampaignClaimResult> {
  const r = await fetch(apiPath("/api/campaign"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "claim", token, progress }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || `claim ${r.status}`), { data, status: r.status });
  return data as CampaignClaimResult;
}

export type AdminCampaignNode = CampaignNodeView & {
  createdAt?: string;
  updatedAt?: string;
};

export async function fetchAdminCampaignTree(): Promise<{ nodes: AdminCampaignNode[] }> {
  const r = await fetch(apiPath("/api/admin-campaign"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin-campaign ${r.status}`);
  return r.json();
}

export async function fetchAdminCampaignCosmetics(): Promise<
  Array<{ id: string; name: string; category: string; campaignOnly: boolean; sentOnly: boolean }>
> {
  const r = await fetch(`${apiPath("/api/admin-campaign")}?section=cosmetics`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`admin-campaign cosmetics ${r.status}`);
  const data = await r.json();
  return Array.isArray(data.cosmetics) ? data.cosmetics : [];
}

export async function createAdminCampaignNode(body: Record<string, unknown>): Promise<AdminCampaignNode> {
  const r = await fetch(apiPath("/api/admin-campaign"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `create ${r.status}`);
  return data.node as AdminCampaignNode;
}

export async function updateAdminCampaignNode(body: Record<string, unknown>): Promise<AdminCampaignNode> {
  const r = await fetch(apiPath("/api/admin-campaign"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `update ${r.status}`);
  return data.node as AdminCampaignNode;
}

export async function deleteAdminCampaignNode(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-campaign")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`delete ${r.status}`);
}

export async function createAdminCampaignCosmetic(body: Record<string, unknown>): Promise<{
  id: string;
  name: string;
  category: string;
}> {
  const r = await fetch(apiPath("/api/admin-campaign"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "cosmetic", ...body }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `cosmetic ${r.status}`);
  return data.cosmetic;
}
